import { Button, Icon, message, Popconfirm } from 'antd';
import { PaginationProps } from 'antd/lib/pagination';
import Table, { ColumnProps } from 'antd/lib/table';
import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IPageResult } from 'src/entity/BaseResult';
import { IStaffDetail, IStaffSearch } from 'src/entity/DepartmentEntity';
import DepartmentAdminService, { StaffRoleDict } from 'src/services/DepartmentAdminService';import I18N from 'src/services/I18NService';

interface IProps extends RouteComponentProps<{}, {}, {useCache: boolean}> {
  isDomestic: boolean,
  isAgent: boolean,
}

const UserAdminListPage: React.FC<IProps> = (props) => {
  const {
    isDomestic,
    location,
    match,
    history,
    isAgent,
  } = props;

  // table 列配置
  const columns: Array<ColumnProps<IStaffDetail>> = useMemo(() => {
    const result: Array<ColumnProps<IStaffDetail>> = [
      {
        title: I18N.get('common_rowNum'),
        key: 'index',
        dataIndex: 'index',
      },
      {
        title: I18N.get('login_userName'),
        key: 'username',
        dataIndex: 'username',
      },
      {
        title: I18N.get('userAdmin_role'),
        key: 'role',
        dataIndex: 'role',
        render: (text: any, record: IStaffDetail, index: number): React.ReactNode => {
          let desc = '';
          if (isAgent) {
            const matchedItem = StaffRoleDict.find(item => item.value === record.role);
            if (matchedItem) {
              desc = I18N.get(matchedItem.key);
            }
          } else {
            desc = I18N.get('userAdmin_operator');
          }
          return (desc);
        }
      },
      // {
      //     title: I18N.get('common_name'),
      //     key: 'name',
      //     dataIndex: 'name',
      // },
      {
        title: isDomestic ? I18N.get('common_mobile') : 'email',
        key: isDomestic ? 'mobile' : 'email',
        dataIndex: isDomestic ? 'mobile' : 'email',
      },
      {
        title: I18N.get('common_status'),
        key: 'is_active',
        dataIndex: 'is_active',
        render: (text: any, record: IStaffDetail, index: number): React.ReactNode => {
          const desc = record.is_active ? I18N.get('common_status_start') : I18N.get('common_status_disable');
          return (<span style={{color: record.is_active ? '#52c41a' : '#ff4d4f'}}>{desc}</span>)
        }
      },
      {
        title: I18N.get('common_operation'),
        dataIndex: 'operation',
        key: 'operation',
        render: (text: any, record: IStaffDetail, index: number): React.ReactNode => {
          const title = record.is_active ? 'common_status_disable' : 'common_status_start';
          return (
            <div>
              {/* <a onClick={() => this.handleOperatorEdit(record)}>{I18N.get('common_edit')}</a> */}
              <Popconfirm
                  title={`是否确认${I18N.get(title)}该用户?`}
                  onConfirm={() => handleStatusToggle(record)}
                  okText={I18N.get('common_ok')}
                  cancelText={I18N.get('common_cancel')}
                >
                  <a>{I18N.get(title)}</a>
              </Popconfirm>
              {/* <a onClick={() => this.handleDelete(record)}>&nbsp;&nbsp;{I18N.get('common_delete')}</a> */}
            </div>
          );
        },
      },
    ];
    return result;
  }, [isDomestic]);

  const [loading, setLoading] = useState(false);

  // 初始化用户列表数组
  const [staffList, setStaffList] = useState([] as IStaffDetail[]);

  // 初始化用户查询参数
  const [staffQuery, setStaffQuery] = useState(() => {
    const { state } = location;
    let result: Partial<IStaffSearch> = new IStaffSearch(10);
    if (state && state.useCache) {
      // 只有从详情页面返回才使用缓存, 使用上一次查询参数
      result = DepartmentAdminService.getStaffQuery(10);
    };
    return result;
  });

  // 初始化分页器配置
  const [pagination, setPagination] = useState(() => {
    const {limit, offset} = staffQuery;
    const current = Math.floor((offset as number) / (limit as number)) + 1;
    const pageSize = limit;
    const initialPagination = DepartmentAdminService.initPagination(current, pageSize);
    return initialPagination;
  })


  useEffect(() => {
    // 第一次渲染时 => 查询 用户列表
    doSearch(staffQuery);
  }, []);


  /** 
   * 执行查询
   */
  const doSearch = async (query: Partial<IStaffSearch>) => {
    setLoading(true);
    setStaffQuery(query);
    let res: IPageResult<IStaffDetail[]> | undefined;
    if (isAgent) {
      res = await DepartmentAdminService.fetchDepartmentStaffs(query);
    } else {
      res = await DepartmentAdminService.fetchDrugstoreStaffs(query);
    }
    
    if (res) {
      const current = Math.floor((query.offset as number) / (query.limit as number)) + 1;
      const newPagination: PaginationProps = {
        ...pagination,
        current,
        pageSize: query.limit,
        total: res.count
      };
      setPagination(newPagination);
      // 构建列表数据
      const tableList = res.results.map((item, index) => {
        const resultItem: IStaffDetail = {
          ...item,
          index: (query.offset as number) + index + 1,
        }
        return resultItem;
      })
      setStaffList(tableList);
    }
    setLoading(false);
  };

  /**
   * 新增按钮处理事件
   * @param record
   */
  const handleOperatorAdd = (): void => {
    history.push(`${match.path}/add`);
  };

  /**
   * 分页器变化回调函数
   */
  const handlePaginationChange = (paginationTable: PaginationProps) => {
    const {current, pageSize} = paginationTable;
    const param: Partial<IStaffSearch> = {
      ...staffQuery,
      offset: ((current || 1) - 1) * (pageSize as number),
      limit: pageSize as number,
    };
    doSearch(param);
  };

  /**
   * 用户[启用|禁用]状态切换
   */
  const handleStatusToggle = async (record: IStaffDetail) => {
    const toggleActive = !record.is_active;
    let isSuccessed = false;
    if (isAgent) {
      isSuccessed = await DepartmentAdminService.toggleDepartmentStaffStatus(record.uuid, toggleActive);
    } else {
      isSuccessed = await DepartmentAdminService.toggleDrugstoreStaffStatus(record.uuid, toggleActive);
    }
    if (isSuccessed) {
      const messageText = toggleActive ? 'common_status_start' : 'common_status_disable';
      message.success(`${I18N.get(messageText)}${I18N.get('common_success')}`);
      setStaffList(oldList => {
        const newList: IStaffDetail[] = oldList.map(item => {
          const newItem = {...item};
          if (newItem.uuid === record.uuid) {
            newItem.is_active = toggleActive;
          }
          return newItem;
        });
        return newList;
      });
    }
  }

  const getTotalText = (): string => {
    let result = '';
    if (pagination.total) {
      result = I18N.get('common_total_prefix') + pagination.total + I18N.get('userAdmin_total_suffix');
    }
    return result;
  };

  return (
    <div>
      <div style={{ margin: '12px' }} className="clearfix">
        <span style={{ float: 'left', lineHeight: '32px', color: '#1fb8cd' }}>{getTotalText()}</span>
        <Button type="primary" onClick={handleOperatorAdd} loading={loading} style={{ float: 'right' }}>
          <Icon type="form" />
          {I18N.get('common_new')}
        </Button>
      </div>
      <Table
        loading={loading}
        columns={columns}
        rowKey='uuid'
        pagination={pagination}
        dataSource={staffList}
        onChange={handlePaginationChange}
      />
    </div>
  )
}
export default UserAdminListPage;



// WEBPACK FOOTER //
// ./src/components/views/UserAdmin/UserAdminListPage.tsx